import React, { useEffect, useId, useState } from 'react';
import rovquest_logo from 'assets/images/rovquest_logo_primary.png';
import MainOnboard from '../mainOnboard';
import forgot_password from 'assets/icons/forgot_password.svg';
import password_on from 'assets/icons/password-on.svg';
import password_off from 'assets/icons/password-off.svg';
import back from 'assets/icons/back.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import PasswordStrength from './passwordStrength';
import axios from 'axios';
import { API_BASE_URL } from 'config/constant';
import { languageHeaders } from 'config/requestLanguage';
import { useTranslation } from 'react-i18next';
import { useRequiredFields } from 'reusable/useRequiredFields';
import { normalizePassword, passwordsMatch } from 'config/passwordRules';
import PasswordRequirements, { usePasswordRuleError } from '../PasswordRequirements';

const NewPassword: React.FC = () => {
  const { t } = useTranslation('login');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();
  const email = (location.state as { email: string })?.email;
  const token = (location.state as { token: string })?.token;
  const otp = (location.state as { otp: string })?.otp;
  // EV-436。必須の2欄は required ではなく aria-required で伝え、空のまま送ったときだけここで止める。
  const required = useRequiredFields({ password: 'password', confirmPassword: 'confirmPassword' });
  const ruleError = usePasswordRuleError();
  const requirementsId = useId();

  useEffect(() => {
    if (!email || !token || !otp) {
      navigate('/404');
    }
    setErrorMessage('');
  }, [email, token, otp, password, confirmPassword]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!required.check({ password, confirmPassword })) return;

    // 前後の空白・改行は、サーバーと同じく取り除いて比べ、取り除いた値を送る（EV-474）。
    if (!passwordsMatch(password, confirmPassword)) {
      setErrorMessage(t('set-new-password.error-password-match'));
      return;
    }
    // EV-474。判定はサーバー・アプリと同じ規則（config/passwordRules）。足りない条件を名前で出す。
    const unmet = ruleError(password);
    if (unmet) {
      setErrorMessage(unmet);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/reset_password/`,
        {
          email: email,
          password: normalizePassword(password),
          token: token,
          otp: otp,
        },
        // EV-271。表示言語を送り、エラー文の言語をそろえる。
        { headers: languageHeaders() }
      );

      if (response.status === 200) {
        navigate('/login');
      } else {
        setErrorMessage(t('set-new-password.error-password-reset'));
      }
    } catch (error: any) {
      console.error(
        'Error verifying OTP:',
        error.response ? error.response.data : error.message
      );
      // RV-370 の続き（2026-10-12 PM 承認の文言 S6）。招待中の運転者は OTP の再設定を使えない。
      // EV-474。管理画面で判定しない規則（よく使われるパスワード・数字だけ・利用者の情報に似ている）で
      // サーバーが断ったら、その文（表示の言語）を出す。
      const serverPassword = error?.response?.status === 400 ? error?.response?.data?.password : undefined;
      setErrorMessage(
        error?.response?.data?.code === 'invitation_pending'
          ? t('error_invitation_pending')
          : Array.isArray(serverPassword) && serverPassword.length > 0
          ? serverPassword.filter((item: unknown) => typeof item === 'string').join(' ')
          : t('set-new-password.error-password-reset')
      );
    }
  };

  return (
    <div className="flex min-h-screen">
      <img
        src={rovquest_logo}
        alt="RovQuest Logo"
        className="fixed top-8 left-6 w-64 h-16"
      />
      <div className="flex-1 flex items-center">
        <div className="flex-col justify-start items-center gap-12 inline-flex w-full mx-20">
          <div className="flex-col justify-start items-center gap-6 inline-flex">
            <img src={forgot_password} alt="" />
            <div className="w-full flex-col justify-start items-center gap-2 inline-flex">
              <div className="text-black text-[33px] font-bold leading-10">
                {t('set-new-password.title')}
              </div>
              <div className="self-stretch text-center text-neutral/100 text-subtitle-1 font-medium">
                {t('set-new-password.subtitle')}
              </div>
            </div>
          </div>
          <form
            onSubmit={handleSubmit}
            className="w-full flex-col justify-start items-center gap-12 inline-flex"
          >
            <div className="flex-col justify-start items-center gap-1 w-full">
              <div className="flex flex-col gap-6">
                <div className="w-full relative">
                  <label
                    htmlFor="password"
                    className="block mb-2 text-sm font-regular text-neutral/100"
                  >
                    {t('set-new-password.password')}
                  </label>
                  <div className="flex items-center relative mb-2">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      className="w-full border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 pl-4 pr-12"
                      placeholder={t('set-new-password.password-holder')}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        required.clear('password');
                      }}
                      {...required.fieldProps('password', { describedBy: requirementsId })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-4"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={t(showPassword ? 'ui.hide-password' : 'ui.show-password', { ns: 'common' })}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? (
                        <img src={password_off} alt="" />
                      ) : (
                        <img src={password_on} alt="" />
                      )}
                    </button>
                  </div>
                  {required.message('password', { className: 'mb-2 text-caption text-error' })}
                  <div className="text-right">
                    <PasswordStrength password={password} />
                  </div>
                </div>
                <div className="w-full relative">
                  <label
                    htmlFor="confirmPassword"
                    className="block mb-2 text-sm font-regular text-neutral/100"
                  >
                    {t('set-new-password.re-password')}
                  </label>
                  <div className="flex items-center relative mb-2">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      className="w-full border border-neutral/50 text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 pl-4 pr-12"
                      placeholder={t('set-new-password.password-holder')}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        required.clear('confirmPassword');
                      }}
                      {...required.fieldProps('confirmPassword')}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-4"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      aria-label={t(showConfirmPassword ? 'ui.hide-password' : 'ui.show-password', { ns: 'common' })}
                      aria-pressed={showConfirmPassword}
                    >
                      {showConfirmPassword ? (
                        <img src={password_off} alt="" />
                      ) : (
                        <img src={password_on} alt="" />
                      )}
                    </button>
                  </div>
                  {required.message('confirmPassword', { className: 'mb-2 text-caption text-error' })}
                </div>
                <PasswordRequirements id={requirementsId} password={password} confirm={confirmPassword} />
              </div>
            </div>
            <div className="flex flex-col w-full">
              {/* 2026-10-26。文字は red-700（以前の text-error は、薄い赤の背景でコントラストが 4.5 に届かなかった。axe）。 */}
              {errorMessage && (
                <div role="alert" className="bg-error/[0.1] text-red-700 px-4 py-3 rounded-lg relative mb-4">
                  <span className="block sm:inline">{errorMessage}</span>
                </div>
              )}
              <button
                type="submit"
                className="flex w-full h-16 rounded-lg justify-center items-center bg-primary/300 text-title text-white"
              >
                {t('set-new-password.button')}
              </button>
            </div>
          </form>

          <a className="flex items-center gap-2" href="/login">
            <img src={back} alt="" />
            <div className="text-subtitle-1 text-neutral/300">
              {t('set-new-password.back')}
            </div>
          </a>
        </div>
      </div>
      <MainOnboard variant="safety" />
    </div>
  );
};

export default NewPassword;
